import { z } from "zod";
import { pageOf, pageQuerySchema } from "../common/pagination.js";
import { localDateSchema, moneySchema, quantitySchema, timestampSchema, unitPriceSchema, uuidSchema, } from "../common/primitives.js";
import { posLineKindSchema } from "../pos/enums.js";
import { fleetBillingRunStatusSchema } from "./enums.js";
/**
 * Filo KULLANIM DEFTERİ (ADR 0017 §13): append-only, kaynak (modül, belge türü, belge, satır) tekil. Worker kasa satışı /
 * iptal / ödeme düzeltmesi ve vardiyanın elle girilen filo fişi olaylarından yazar. Satır filoya yazılan TUTARI taşır
 * (karma ödemede filo payı); iptal / iade karşı kaydı NEGATİFTİR ve asıl satışın ÖKC fiş bağını + anını taşır.
 */
export const FLEET_USAGE_SOURCE_DOC_TYPES = [
    "POS_SALE",
    "POS_RETURN",
    "POS_RETENDER",
    "SHIFT_CREDIT_SALE",
    "SHIFT_CREDIT_SALE_CANCEL",
    // Prompt 19: kasa dışında kapanan şarj oturumu (oturum başına faturalanır; dönem faturasına girmez).
    "EV_SESSION",
];
export const fleetUsageSourceDocTypeSchema = z.enum(FLEET_USAGE_SOURCE_DOC_TYPES);
export const FLEET_USAGE_SOURCE_LABELS = {
    POS_SALE: "Kasa satışı",
    POS_RETURN: "Kasa iptali / iadesi",
    POS_RETENDER: "Ödeme türü düzeltmesi",
    SHIFT_CREDIT_SALE: "Elle filo fişi",
    SHIFT_CREDIT_SALE_CANCEL: "Elle filo fişi iptali",
    EV_SESSION: "Şarj oturumu (oturum faturası)",
};
export const fleetUsageEntrySchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    stationId: uuidSchema,
    stationName: z.string(),
    counterpartyId: uuidSchema,
    vehicleId: uuidSchema.nullable(),
    plate: z.string().nullable().describe("KVKK (maskeli olabilir)"),
    fleetDriverId: uuidSchema.nullable(),
    driverName: z.string().nullable().describe("KVKK (maskeli olabilir)"),
    productId: uuidSchema.nullable(),
    productName: z.string().nullable(),
    lineKind: posLineKindSchema,
    quantity: quantitySchema,
    amount: moneySchema,
    odometerKm: z.number().int().nullable(),
    occurredAt: timestampSchema,
    localDay: localDateSchema,
    sourceModule: z.enum(["POS", "SHIFTS", "EV"]),
    sourceDocType: fleetUsageSourceDocTypeSchema,
    sourceDocId: uuidSchema,
    posSaleId: uuidSchema.nullable(),
    shiftCreditSaleId: uuidSchema.nullable(),
    receipt: z
        .object({ fiscalDeviceSerial: z.string(), zNo: z.number().int(), receiptNo: z.number().int() })
        .nullable()
        .describe("ÖKC fiş bağı; yoksa (elle fiş) dönem faturasına GİRMEZ"),
    violations: z.array(z.string()),
    overridden: z.boolean(),
    billingRunId: uuidSchema.nullable().describe("Etkin dönem faturası satırı varsa"),
    personalDataMasked: z.boolean(),
});
export const fleetUsageEntryPageSchema = pageOf(fleetUsageEntrySchema);
export const listFleetUsageQuerySchema = pageQuerySchema.extend({
    counterpartyId: uuidSchema.optional(),
    vehicleId: uuidSchema.optional(),
    stationId: uuidSchema.optional(),
    from: localDateSchema.optional().describe("İstanbul günü (dâhil)"),
    to: localDateSchema.optional().describe("İstanbul günü (dâhil)"),
});
// ── Ekstre ──────────────────────────────────────────────────────────────────
export const fleetStatementQuerySchema = z
    .object({
    from: localDateSchema.optional().describe("Varsayılan: bu ayın ilk günü"),
    to: localDateSchema.optional().describe("Varsayılan: bugün"),
})
    .refine((value) => !value.from || !value.to || value.from <= value.to, {
    path: ["to"],
    message: "Bitiş başlangıçtan önce olamaz",
});
export const fleetStatementGroupSchema = z.object({
    vehicleId: uuidSchema.nullable(),
    plate: z.string().nullable(),
    fleetDriverId: uuidSchema.nullable(),
    driverName: z.string().nullable(),
    litres: quantitySchema,
    amount: moneySchema,
    transactionCount: z.number().int(),
});
export const fleetVehicleConsumptionSchema = z.object({
    vehicleId: uuidSchema,
    plate: z.string(),
    litres: quantitySchema.describe("Akaryakıt satırlarının litresi (filo payı)"),
    amount: moneySchema,
    transactionCount: z.number().int(),
    firstOdometerKm: z.number().int().nullable(),
    lastOdometerKm: z.number().int().nullable(),
    distanceKm: z.number().int().nullable(),
    consumptionPer100Km: z.string().nullable().describe("L / 100 km (doludan doluya; 2 ondalık)"),
});
export const fleetStatementRunSchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    periodFrom: localDateSchema,
    periodTo: localDateSchema,
    status: fleetBillingRunStatusSchema,
    totalAmount: moneySchema,
    invoiceId: uuidSchema.nullable(),
    invoiceNo: z.string().nullable(),
    invoiceStatus: z.string().nullable(),
});
export const fleetStatementSchema = z.object({
    counterpartyId: uuidSchema,
    counterpartyName: z.string(),
    from: localDateSchema,
    to: localDateSchema,
    totals: z.object({
        litres: quantitySchema,
        amount: moneySchema,
        transactionCount: z.number().int(),
        unbilledAmount: moneySchema.describe("Etkin dönem faturasına girmemiş tutar"),
    }),
    groups: z.array(fleetStatementGroupSchema).describe("Araç × sürücü kırılımı"),
    vehicles: z.array(fleetVehicleConsumptionSchema),
    billingRuns: z.array(fleetStatementRunSchema).describe("Dönemi aralıkla kesişen dönem faturaları"),
    personalDataMasked: z.boolean(),
});
// ── Tüketim raporu ──────────────────────────────────────────────────────────
export const fleetConsumptionQuerySchema = z
    .object({
    counterpartyId: uuidSchema.optional(),
    from: localDateSchema,
    to: localDateSchema,
})
    .refine((value) => value.from <= value.to, { path: ["to"], message: "Bitiş başlangıçtan önce olamaz" });
export const fleetConsumptionItemSchema = fleetVehicleConsumptionSchema.extend({
    counterpartyId: uuidSchema,
    counterpartyName: z.string(),
    averageUnitPrice: unitPriceSchema.nullable().describe("Akaryakıt tutarı / litre (₺, 4 ondalık)"),
});
export const fleetConsumptionReportSchema = z.object({
    from: localDateSchema,
    to: localDateSchema,
    items: z.array(fleetConsumptionItemSchema),
    withoutVehicle: z
        .object({ litres: quantitySchema, amount: moneySchema, transactionCount: z.number().int() })
        .describe("Araç bağı olmayan kullanım (araçsız filo fişi)"),
    totals: z.object({ litres: quantitySchema, amount: moneySchema, transactionCount: z.number().int() }),
    personalDataMasked: z.boolean(),
});
//# sourceMappingURL=usage.js.map